import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StaffLink from '../../components/shared/StaffLink';
import {
  CalendarDaysIcon, PlusIcon, XMarkIcon, CheckCircleIcon,
  ExclamationCircleIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

// Parse a Postgres DATE (either "2026-07-13" or "2026-07-13T00:00:00.000Z")
// as a calendar date, not a UTC instant. Otherwise `new Date("2026-07-13")`
// becomes UTC midnight which renders as "Sun, Jul 12" in ET — the classic
// off-by-one that made Sandra's Mon 7/13 request show as Sun 7/12.
const parseDateOnly = (d) => {
  if (!d) return null;
  const s = String(d).slice(0, 10); // "2026-07-13"
  const [y, m, day] = s.split('-').map(Number);
  return new Date(y, m - 1, day); // local midnight — safe for rendering
};
const formatDate = (d) => {
  const dt = parseDateOnly(d);
  return dt ? dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—';
};
const formatRange = (start, end) => {
  if (!end || end === start) return formatDate(start);
  return `${formatDate(start)} – ${formatDate(end)}`;
};
const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
};

export default function TimeOffPage() {
  const { user, isManagement } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showDecide, setShowDecide] = useState(null); // { request, decision: 'approve'|'deny' }
  const [editRequest, setEditRequest] = useState(null); // request being date-edited
  const [filter, setFilter] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/time-off');
      setRequests(data);
    } catch { toast.error('Failed to load requests'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = requests.filter(r => filter === 'all' || r.status === filter);

  const cancel = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try {
      await api.patch(`/time-off/${id}/cancel`);
      toast.success('Cancelled');
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="animate-slide-up">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDaysIcon className="w-6 h-6" /> Time-Off Requests
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {isManagement ? 'Approve or deny requests from your team.' : 'Submit a request and the director will review it.'}
          </p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 font-medium">
          <PlusIcon className="w-5 h-5" /> New Request
        </button>
      </header>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {['pending', 'approved', 'denied', 'cancelled', 'all'].map(f => {
          const count = f === 'all' ? requests.length : requests.filter(r => r.status === f).length;
          return (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {f.charAt(0).toUpperCase() + f.slice(1)} {count > 0 && <span className="ml-1 text-xs opacity-70">({count})</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <Spinner />
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-400">
          No {filter !== 'all' ? filter : ''} requests.
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map(r => (
            <RequestCard key={r.id} req={r} currentUser={user} isManagement={isManagement}
              onDecide={(decision) => setShowDecide({ request: r, decision })}
              onEdit={() => setEditRequest(r)}
              onCancel={() => cancel(r.id)} />
          ))}
        </div>
      )}

      {showForm && <NewRequestModal onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {showDecide && (
        <DecideModal data={showDecide}
          onClose={() => setShowDecide(null)}
          onSaved={() => { setShowDecide(null); load(); }} />
      )}
      {editRequest && (
        <EditDatesModal request={editRequest}
          onClose={() => setEditRequest(null)}
          onSaved={() => { setEditRequest(null); load(); }} />
      )}
    </div>
  );
}

function RequestCard({ req, currentUser, isManagement, onDecide, onEdit, onCancel }) {
  const isOwn = req.staff_user_id === currentUser.id;
  const tone = {
    pending: 'amber', approved: 'green', denied: 'red', cancelled: 'gray',
  }[req.status] || 'gray';
  const toneClass = {
    amber: 'bg-amber-50 border-amber-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    gray: 'bg-gray-50 border-gray-200',
  }[tone];
  const badgeClass = {
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    gray: 'bg-gray-200 text-gray-700',
  }[tone];

  return (
    <div className={`rounded-2xl border p-5 ${toneClass}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="font-semibold text-gray-900">
            <StaffLink id={req.staff_user_id} name={`${req.staff_first} ${req.staff_last}`} />
          </p>
          <p className="text-sm text-gray-700 mt-1">
            <strong>{formatRange(req.start_date, req.end_date)}</strong>
            {' · '}
            {req.full_day ? 'Full day(s) off' : `${formatTime(req.start_time)} – ${formatTime(req.end_time)}`}
          </p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeClass}`}>{req.status}</span>
      </div>

      {req.reason && (
        <p className="text-sm text-gray-600 italic mt-2">"{req.reason}"</p>
      )}

      {req.decision_notes && (
        <div className="mt-3 bg-white/60 rounded-lg p-2 text-xs text-gray-700">
          <strong>Decision note:</strong> {req.decision_notes}
          {req.decided_by_first && <span className="text-gray-500"> — {req.decided_by_first} {req.decided_by_last || ''}</span>}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
        <span>Submitted {new Date(req.requested_at).toLocaleString()}</span>
        <div className="flex gap-2">
          {isOwn && req.status === 'pending' && (
            <button onClick={onCancel} className="text-xs text-gray-500 hover:text-red-500">Cancel request</button>
          )}
          {isManagement && (req.status === 'pending' || req.status === 'approved') && (
            <button onClick={onEdit}
              title="Fix wrong dates or times on this request"
              className="text-xs bg-white text-gray-600 border border-gray-300 px-3 py-1 rounded-lg hover:bg-gray-50 font-medium">
              Edit dates
            </button>
          )}
          {isManagement && req.status === 'pending' && !isOwn && (
            <>
              <button onClick={() => onDecide('deny')}
                className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-lg hover:bg-red-200 font-medium inline-flex items-center gap-1">
                <ExclamationCircleIcon className="w-3.5 h-3.5" /> Deny
              </button>
              <button onClick={() => onDecide('approve')}
                className="text-xs bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600 font-medium inline-flex items-center gap-1">
                <CheckCircleIcon className="w-3.5 h-3.5" /> Approve
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NewRequestModal({ onClose, onSaved }) {
  const { register, handleSubmit, watch } = useForm({ defaultValues: { full_day: 'true' } });
  const [saving, setSaving] = useState(false);
  const fullDay = watch('full_day') === 'true';

  const submit = async (form) => {
    setSaving(true);
    try {
      await api.post('/time-off', {
        start_date: form.start_date,
        end_date: form.end_date || form.start_date,
        full_day: form.full_day === 'true',
        start_time: form.full_day === 'true' ? null : form.start_time,
        end_time: form.full_day === 'true' ? null : form.end_time,
        reason: form.reason || null,
      });
      toast.success('Request submitted — you\'ll get an email when the director decides');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New Time-Off Request" onClose={onClose}>
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
          <div className="flex gap-2">
            <label className="flex-1">
              <input type="radio" {...register('full_day')} value="true" className="sr-only peer" defaultChecked />
              <div className="border border-gray-200 rounded-xl px-3 py-2 text-center text-sm cursor-pointer peer-checked:border-orange-500 peer-checked:bg-orange-50 peer-checked:text-orange-700">
                Full Day(s)
              </div>
            </label>
            <label className="flex-1">
              <input type="radio" {...register('full_day')} value="false" className="sr-only peer" />
              <div className="border border-gray-200 rounded-xl px-3 py-2 text-center text-sm cursor-pointer peer-checked:border-orange-500 peer-checked:bg-orange-50 peer-checked:text-orange-700">
                Partial Hours
              </div>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
            <input type="date" min={DATE_FLOOR} {...register('start_date', { required: true })}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date {fullDay ? '(optional)' : '*'}</label>
            <input type="date" min={DATE_FLOOR} {...register('end_date')}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
          </div>
        </div>

        {!fullDay && (
          <div className="grid grid-cols-2 gap-3 bg-orange-50/30 p-3 rounded-xl border border-orange-100">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
              <input type="time" {...register('start_time', { required: !fullDay })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
              <input type="time" {...register('end_time', { required: !fullDay })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none" />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
          <textarea {...register('reason')} rows={3}
            placeholder="e.g. doctor's appointment, family event, travel"
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
        </div>

        <p className="text-xs text-gray-500">
          The director will review your request and you'll get an email with the decision. Per policy, time-off requests are due Wednesday at 5pm, 2 weeks in advance.
        </p>

        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving}
            className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50">
            {saving ? 'Submitting…' : 'Submit Request'}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

// Edit the dates/times on a time-off request that got stored with the
// wrong day. Common cause: submit path timezone bug we haven't yet
// pinpointed. Rather than block the director on the hunt, let her fix
// it in-place.
function EditDatesModal({ request, onClose, onSaved }) {
  const iso = (v) => v ? String(v).slice(0, 10) : '';
  const hhmm = (v) => v ? String(v).slice(0, 5) : '';
  const [startDate, setStartDate] = useState(iso(request.start_date));
  const [endDate, setEndDate] = useState(iso(request.end_date));
  const [fullDay, setFullDay] = useState(!!request.full_day);
  const [startTime, setStartTime] = useState(hhmm(request.start_time));
  const [endTime, setEndTime] = useState(hhmm(request.end_time));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!startDate) return toast.error('Start date required');
    if (endDate && endDate < startDate) return toast.error('End date is before start date');
    if (!fullDay && (!startTime || !endTime)) return toast.error('Partial-day needs start + end time');
    setSaving(true);
    try {
      await api.patch(`/time-off/${request.id}`, {
        start_date: startDate,
        end_date: endDate || startDate,
        full_day: fullDay,
        start_time: fullDay ? null : startTime,
        end_time: fullDay ? null : endTime,
      });
      toast.success('Request updated. If already approved, schedule was re-synced.');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit — ${request.staff_first} ${request.staff_last}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="text-xs text-gray-500 bg-gray-50 rounded-xl p-3">
          Fix wrong dates or times on this request. If already approved, saving here
          also re-syncs the schedule grid (removes stale off cells, adds new ones).
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start date *</label>
            <input type="date" min={DATE_FLOOR} value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
            <input type="date" min={DATE_FLOOR} value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={fullDay} onChange={(e) => setFullDay(e.target.checked)} />
          Full day(s) off
        </label>
        {!fullDay && (
          <div className="grid grid-cols-2 gap-3 bg-orange-50/30 p-3 rounded-xl border border-orange-100">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm" />
            </div>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving}
            className="flex-1 bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={onClose}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function DecideModal({ data, onClose, onSaved }) {
  const { request: r, decision } = data;
  const isApprove = decision === 'approve';
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null); // null = loading, [] = none, [...] = list

  // Pre-fetch other staff who already have approved time-off overlapping
  // these dates, so the director sees the broader staffing picture before
  // approving and accidentally leaving the room uncovered.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: rows } = await api.get('/time-off/conflicts', {
          params: {
            start_date: r.start_date.slice(0, 10),
            end_date: r.end_date.slice(0, 10),
            exclude_staff_id: r.staff_user_id,
          },
        });
        if (!cancelled) setConflicts(rows);
      } catch {
        if (!cancelled) setConflicts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [r.id, r.start_date, r.end_date, r.staff_user_id]);

  const submit = async () => {
    if (!isApprove && !notes.trim()) {
      toast.error('Please add a note explaining the denial');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/time-off/${r.id}/${decision}`, { decision_notes: notes || null });
      toast.success(isApprove ? 'Approved' : 'Denied');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={isApprove ? 'Approve Time-Off' : 'Deny Time-Off'} onClose={onClose}>
      <div className="space-y-4">
        <div className={`rounded-xl p-3 ${isApprove ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <p className="font-medium text-gray-900">{r.staff_first} {r.staff_last}</p>
          <p className="text-sm text-gray-700 mt-1">
            {formatRange(r.start_date, r.end_date)}
            {' · '}
            {r.full_day ? 'Full day(s)' : `${formatTime(r.start_time)} – ${formatTime(r.end_time)}`}
          </p>
          {r.reason && <p className="text-xs text-gray-600 italic mt-1">"{r.reason}"</p>}
        </div>

        {/* Who else is off these days */}
        {conflicts === null ? (
          <p className="text-xs text-gray-400">Checking who else is off these days…</p>
        ) : conflicts.length === 0 ? (
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            ✓ No one else has approved time off during these dates.
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-xs font-semibold text-amber-900 mb-1">
              ⚠ {conflicts.length} other staff already off during this range:
            </p>
            <ul className="text-xs text-amber-900 space-y-0.5">
              {conflicts.map(c => (
                <li key={c.id}>
                  <span className="font-medium">{c.first_name} {c.last_name}</span>
                  <span className="text-amber-700"> — {formatRange(c.start_date, c.end_date)}</span>
                  {!c.full_day && (
                    <span className="text-amber-700"> ({formatTime(c.start_time)} – {formatTime(c.end_time)})</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-amber-700 mt-1.5">
              Approving anyway? Make sure coverage is sorted out for those days.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note to staff {isApprove ? '(optional)' : '*'}
          </label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder={isApprove ? 'e.g. Have a great time off!' : 'Reason — required so the staff member understands'}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
        </div>

        <p className="text-xs text-gray-500">
          {isApprove
            ? 'On approval, the schedule overrides will be inserted for each affected date — the team grid + clock-in webhook will reflect the time off.'
            : 'The staff member will receive an email with your note explaining the denial.'}
        </p>

        <div className="flex gap-2 pt-1">
          <button onClick={submit} disabled={saving}
            className={`flex-1 py-2.5 rounded-xl font-semibold disabled:opacity-50 ${
              isApprove ? 'bg-green-500 text-white hover:bg-green-600'
                        : 'bg-red-500 text-white hover:bg-red-600'
            }`}>
            {saving ? 'Saving…' : (isApprove ? 'Approve' : 'Deny')}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function Spinner() {
  return <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" /></div>;
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
